/** @format */
import { AxiosRequestConfig, AxiosResponse } from 'axios';

import { MarketCode } from '@toucan/config/markets';

import { Checkout } from '@toucan/types/mpbx/checkout';

import { client } from '@toucan/services/request-client';
import { rejectIfInvalidCSRFResponse } from '@toucan/services/requests/csrf';

import { centToDecimal } from '@toucan/helpers/currency';

import { transformOutboundAddressData } from '@toucan/client/core/address';
import {
    DeliveryOptionsResponse,
    ShippingOptionType,
    transformShippingOptions,
} from '@toucan/client/core/shipping';
import { Address, TransactionVariant } from '@toucan/client/global/transaction';

export async function submitShippingAddress(
    checkoutId: string,
    shippingAddress: Address,
    market: MarketCode,
    csrfToken: string
): Promise<AxiosResponse<Checkout>> {
    const config: AxiosRequestConfig = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Market': market.toLowerCase(),
            'CSRF-Token': csrfToken,
        },
    };

    const response = await client.post(
        `/checkout/${checkoutId}/add-delivery-address/`,
        transformOutboundAddressData(shippingAddress),
        config
    );

    return rejectIfInvalidCSRFResponse(response);
}

export async function submitShippingOption(
    checkoutId: string,
    optionId: number,
    date: string | null,
    market: MarketCode,
    csrfToken: string
): Promise<AxiosResponse<Checkout>> {
    const config: AxiosRequestConfig = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Market': market.toLowerCase(),
            'CSRF-Token': csrfToken,
        },
    };

    const deliveryOptionData: {
        option_id: number;
        collection_date?: string;
    } = {
        option_id: optionId,
    };

    if (date) {
        deliveryOptionData.collection_date = date;
    }

    const checkout = await client.post(
        `/checkout/${checkoutId}/delivery-option/`,
        deliveryOptionData,
        config
    );

    return rejectIfInvalidCSRFResponse(checkout);
}
export async function fetchDeliveryOptions(
    market: MarketCode,
    {
        country,
        region,
        transactionValueInCents,
        transactionVariant,
    }: {
        country: string; // 2-letter country code
        region: string | undefined; // Delivery region (UK Postcode, US state)
        transactionValueInCents?: number;
        transactionVariant?: TransactionVariant;
    },
    checkoutId?: string,
    csrfToken?: string
) {
    const transactionValueInDecimal = transactionValueInCents
        ? centToDecimal(transactionValueInCents)
        : undefined;
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
            'CSRF-Token': csrfToken ?? '',
        },
    };
    const response = await client.post<DeliveryOptionsResponse>(
        // New checkout service is always checkoutId aware, but for backwards
        // compatibility we still need to support the old endpoint usage for now.
        checkoutId
            ? `/checkout/${checkoutId}/delivery-options/`
            : '/couriers/v1/public/delivery/options',
        {
            delivery_country: country,
            delivery_region: region,
            transaction_value: transactionValueInDecimal,
            transaction_variant: transactionVariant,
        },
        config
    );
    return transformShippingOptions(response.data, ShippingOptionType.SHIPPING);
}
